/**
 * LIVE means frames are arriving. Nothing else.
 *
 * The predecessor bound the badge to the telemetry link, so an aircraft whose
 * radio was up and whose camera was not read LIVE over a black rectangle. Its
 * fix polled a frame counter on the server. This server never sees a frame, so
 * the count comes from the player: WebRTC's inbound-rtp `framesDecoded`, the
 * video element's decoded-frame count, or the MJPEG reader's own tally. The
 * badge says LIVE only while that number is going up.
 */

import { useEffect, useRef, useState } from 'react';

export type LiveState = 'connecting' | 'live' | 'stalled' | 'failed';

/** No new frame for this long and the picture is frozen, whatever the player thinks. */
export const STALE_AFTER_MS = 3000;

export interface FrameWatch {
  /** The last counter value read, or null before the first read. */
  frames: number | null;
  /** When the counter last went up. Null until it ever has. */
  advancedAt: number | null;
}

export const NO_FRAMES_YET: FrameWatch = { frames: null, advancedAt: null };

/** Fold one counter reading in. A counter that goes backwards is a new stream. */
export function observe(previous: FrameWatch, frames: number | null, now: number): FrameWatch {
  if (frames === null) return previous;
  // The first reading, or one lower than the last, only sets a baseline. A
  // player that reports 40 frames already decoded has not shown that it is
  // decoding any more.
  if (previous.frames === null || frames < previous.frames) {
    return { frames, advancedAt: previous.advancedAt };
  }
  if (frames > previous.frames) return { frames, advancedAt: now };
  return previous;
}

export function liveState(
  watch: FrameWatch,
  now: number,
  { failed = false, waiting = false }: { failed?: boolean; waiting?: boolean } = {},
): LiveState {
  if (failed) return 'failed';
  if (watch.advancedAt === null) return 'connecting';
  if (waiting || now - watch.advancedAt > STALE_AFTER_MS) return 'stalled';
  return 'live';
}

/**
 * Sample a frame counter twice a second and say whether frames are flowing.
 *
 * `read` is called on a timer, not on render, and may change identity freely:
 * the latest one is used. Pass `resetKey` to start over when the stream does.
 */
export function useFrameWatch(
  read: () => number | null,
  { failed = false, waiting = false, resetKey = '' } = {},
): LiveState {
  const readRef = useRef(read);
  readRef.current = read;
  const [watch, setWatch] = useState<FrameWatch>(NO_FRAMES_YET);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setWatch(NO_FRAMES_YET);
    const timer = setInterval(() => {
      const at = Date.now();
      setNow(at);
      setWatch((previous) => observe(previous, readRef.current(), at));
    }, 500);
    return () => clearInterval(timer);
  }, [resetKey]);

  return liveState(watch, now, { failed, waiting });
}

/** Decoded frames of a <video>, where the browser reports them. */
export function videoFrames(video: HTMLVideoElement | null): number | null {
  if (!video) return null;
  const quality = typeof video.getVideoPlaybackQuality === 'function'
    ? video.getVideoPlaybackQuality()
    : null;
  if (quality && quality.totalVideoFrames > 0) return quality.totalVideoFrames;
  // Not every engine counts frames. Playback position only moves while
  // pictures are being presented, so milliseconds of it stand in for frames.
  return video.readyState >= 2 ? Math.floor(video.currentTime * 1000) : null;
}
